import { useId, useState } from 'react';
import { cn } from 'cn';
import type { ReviewRowData } from './reviewTypes';

/**
 * The two state marks: **review** by a person, **check** by a model.
 *
 * They are the screen's central discipline. Two independent facts, each with
 * two values, and they must never collapse into one "trusted" mark — a
 * translation can be approved and unchecked, or checked and unapproved, and
 * those are different claims.
 *
 * **Shape-coded, not hue-coded.** A person icon for review and a shield for
 * the check; filled for done, dashed outline for not. That is what survives
 * High Contrast and colour-blindness, and it is why colour is the last thing
 * carrying meaning here rather than the first.
 *
 * Each carries an icon **and a word**, with the explanation in a popover on
 * hover, focus **and** tap — hover alone exists on neither a phone nor a
 * keyboard.
 */

function PersonIcon({ done }: { done: boolean }) {
  return (
    <svg viewBox="0 0 24 24" width={13} height={13} fill="none" stroke="currentColor"
         strokeWidth={1.9} strokeLinecap="round" strokeLinejoin="round" aria-hidden
         className="shrink-0">
      <path d="M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM4 21a8 8 0 0 1 16 0" />
      {done ? <path d="M16.5 15.5l2 2 4-4" /> : null}
    </svg>
  );
}

function ShieldIcon({ done }: { done: boolean }) {
  return (
    <svg viewBox="0 0 24 24" width={13} height={13} fill="none" stroke="currentColor"
         strokeWidth={1.9} strokeLinecap="round" strokeLinejoin="round" aria-hidden
         className="shrink-0">
      <path d="M12 3l7 3v5c0 4.3-2.9 8.1-7 9-4.1-.9-7-4.7-7-9V6z" />
      {done ? <path d="M9 12l2 2 4-4" /> : null}
    </svg>
  );
}

/**
 * One mark, with its explanation.
 *
 * The explanation matters as much as the mark. "Not yet checked for
 * faithfulness" is accurate and means nothing to anyone who has not read the
 * spec, so each popover says what the thing actually is in a sentence.
 */
function Mark({
  kind,
  done,
  label,
  title,
  explanation,
}: {
  kind: 'review' | 'check';
  done: boolean;
  label: string;
  title: string;
  explanation: string;
}) {
  const [open, setOpen] = useState(false);
  const id = useId();

  return (
    <span className="relative inline-flex">
      <button
        type="button"
        aria-describedby={open ? id : undefined}
        aria-expanded={open}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onClick={() => setOpen((was) => !was)}
        className={cn(
          'inline-flex h-[22px] items-center gap-1 rounded-sm px-1.5',
          'text-[11px] font-semibold leading-none',
          'outline-none focus-visible:ring-3 focus-visible:ring-ring/50',
          done
            // Filled when the thing happened; a dashed outline when it has
            // not. The shape is the signal, the colour only reinforces it.
            ? kind === 'review'
              ? 'bg-status-reviewed-bg text-status-reviewed'
              : 'bg-success-bg text-success'
            : 'border border-dashed border-current text-muted-foreground',
        )}
      >
        {kind === 'review' ? <PersonIcon done={done} /> : <ShieldIcon done={done} />}
        {label}
      </button>

      {open ? (
        <span
          id={id}
          role="tooltip"
          className={cn(
            'absolute top-full z-30 mt-1 w-64 rounded-md border border-border',
            'bg-popover p-2.5 text-popover-foreground shadow-2',
            // Logical, so it opens inward in both directions.
            'start-0',
          )}
        >
          <span className="block text-[12px] font-semibold">{title}</span>
          <span className="mt-1 block text-[12px] leading-[17px] text-muted-foreground">
            {explanation}
          </span>
        </span>
      ) : null}
    </span>
  );
}

export function StateMarks({
  row,
  checkEverRuns,
}: {
  row: ReviewRowData;
  /** False when nothing has ever recorded a check — see the note below. */
  checkEverRuns: boolean;
}) {
  return (
    <span className="flex flex-col items-start gap-1">
      <Mark
        kind="review"
        done={row.reviewed}
        label={row.reviewed ? 'Reviewed' : 'Unreviewed'}
        title={row.reviewed ? 'A person approved this' : 'Nobody has read this yet'}
        explanation={
          row.reviewed
            ? `${row.reviewerName ?? 'A reviewer'} read it and approved it. That is a
               person's judgement, not an automatic test.`
            : `Readers can see this translation, and no one has checked it.
               Approving it is what this queue is for.`
        }
      />
      <Mark
        kind="check"
        done={row.checked}
        label={row.checked ? `Checked${row.flagCount > 0 ? ` · ${row.flagCount}` : ''}` : 'Unchecked'}
        title={
          row.checked
            ? row.flagCount > 0
              ? `${row.flagCount} thing${row.flagCount === 1 ? '' : 's'} flagged`
              : 'Compared against the Arabic, nothing flagged'
            : checkEverRuns
              ? 'The automatic comparison has not run'
              : 'The automatic comparison does not run yet'
        }
        explanation={
          // Said in words a person understands, rather than the spec's
          // phrase. And when the check has never run anywhere, the honest
          // answer is that the feature is off — not that this row is behind.
          checkEverRuns
            ? `A second pass compares the Arabic against the translation and flags
               anything left out, added, or changed in meaning. It never rewrites.
               ${row.checked ? '' : 'It has not run on this one.'}`
            : `A second pass is meant to compare the Arabic against the translation
               and flag anything left out, added or changed. It is not built yet,
               so every translation is unchecked — this row is not behind.`
        }
      />
    </span>
  );
}
